import React, { useState, useMemo } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
} from 'date-fns';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

// Helper to parse numbers from strings, similar to Stats.jsx
const parseNum = (val) => {
  if (!val) return 0;
  if (typeof val === 'number') return val;
  return Number(String(val).replace(/[^\d.-]/g, '')) || 0;
};


// Helper to format currency
const formatCurrency = (val) => {
    if (val == null || isNaN(val)) return "$0.00";
    const formatted = val.toLocaleString("en-US", { style: "currency", currency: "USD" });
    return val < 0 ? `-${formatted.substring(1)}` : formatted;
}

// --- NEW: Helper functions copied from Stats.jsx ---
function getProductShort(val) {
  return val ? val.split(" - ")[0] : "";
}

function isFundingRow(row) {
  const type = String(row?.["Funding Type"] || "").toLowerCase();
  return row?.isFunding === true
    || type === "deposit"
    || type === "withdrawal"
    || getProductShort(row?.["Product"]) === "Funding";
}

function isDidNotTake(val) {
  const s = String(val ?? "").trim().toLowerCase();
  return s === "did not take" || s === "dnt";
}
// --- End of new helper functions ---


const TradingCalendar = ({ journalRows, onDayClick, isPrivacyMode }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // 1. Process journalRows to get daily summaries
  const dailyData = useMemo(() => {
    if (!journalRows || journalRows.length === 0) {
      return new Map();
    }

    const dataMap = new Map();
    journalRows.forEach(row => {
      // --- MODIFIED: Apply same filters as modal ---
      if (isFundingRow(row) || isDidNotTake(row["Position"])) {
        return; // Skip this row, it's not a valid trade
      }
      // --- End of modification ---

      // Create a date object that respects the local timezone
      const dateObj = new Date(`${row.Date}T00:00:00`);
      const dateKey = format(dateObj, 'yyyy-MM-dd');
      const reward = parseNum(row['Actual Reward']);

      if (dataMap.has(dateKey)) {
        const existing = dataMap.get(dateKey);
        existing.pnl += reward;
        existing.tradeCount += 1;
      } else {
        dataMap.set(dateKey, {
          pnl: reward,
          tradeCount: 1,
        });
      }
    });
    return dataMap;
  }, [journalRows]);

  // 2. Calendar grid logic
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const [userPrefs, setUserPrefs] = useState({});

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm p-6 mb-8">
      {/* Header with Navigation */}
      <div className="flex justify-between items-center mb-4">
        <button onClick={handlePrevMonth} className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
          <FaChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
        </button>
        <h2 className="text-xl font-bold text-blue-900 dark:text-blue-200">
          {format(currentDate, 'MMMM yyyy')}
        </h2>
        <button onClick={handleNextMonth} className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
          <FaChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-300" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Weekday Headers */}
        {weekdays.map(day => (
          <div key={day} className="font-bold text-sm text-center text-gray-500 dark:text-gray-400 py-2">
            {day}
          </div>
        ))}

        {/* Day Cells */}
        {days.map(day => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dataForDay = dailyData.get(dateKey);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isCurrentDay = isToday(day);

          return (
            <div
              key={dateKey}
              onClick={() => (onDayClick && isCurrentMonth) ? onDayClick(day) : null}
              className={`
                h-32 p-1.5 border border-gray-200 dark:border-gray-700 rounded-lg flex flex-col text-sm transition-all
                ${isCurrentMonth ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900/50'}
                ${onDayClick && isCurrentMonth ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600' : ''}
              `}
            >
              <div
                className={`
                  font-semibold self-end
                  ${isCurrentDay ? 'bg-blue-600 text-white rounded-full h-6 w-6 flex items-center justify-center' : ''}
                  ${isCurrentMonth ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}
                `}
              >
                {format(day, 'd')}
              </div>

              {dataForDay && isCurrentMonth && (
                <div className="mt-1 text-left text-xs flex-grow space-y-1">
                  <p
                    className={`font-bold ${dataForDay.pnl >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}
                  >
                    {isPrivacyMode ? "***" :formatCurrency(dataForDay.pnl)}
                  </p>
                  <p className="text-gray-500 dark:text-gray-400">
                    {dataForDay.tradeCount} trade{dataForDay.tradeCount !== 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TradingCalendar;